import { Button } from '@mui/material';
import { uniq, uniqBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { CommodityManagementApi } from '../../../../../Apis/Apis';
import { AuditTrailModel, BudgetModel, GetCustomerDataResponse, GetCustomerSettingsResponse, YearDataModel } from '../../../../../Generated/Commodity-Management-Api';
import { ContractDateHelper } from '../../../../../Helpers';
import { ModelToRowTransformer } from '../../../../../Helpers/ModelToRowTransformer';
import { useLoadingState } from '../../../../../Hooks';
import { GroupingOption } from '../../../../../Types';
import { StoneXDivider, StoneXLoading, StoneXRow } from '../../../../StoneX';
import StoneXMainPage from '../../../../StoneX/StoneXMainPage/StoneXMainPage';
import { StoneXSelectPills } from '../../../../StoneXMui';
import { BudgetFilters, BudgetRow } from '../Types';
import BudgetTable from './BudgetTable';
import dayjs from 'dayjs';
import { UpdateBudgetsModal } from '../UpdateBudgetsPage/UpdateBudgetsModal';
import { useApplicationSelector, RootState } from '../../../../../Redux/ReduxStore';
import { AddBudgetsModal } from '../AddBudgetsPage/AddBudgetsModal';
import ViewAuditTrailModal from '../../AuditTrail/AuditTrailModal';
import { BudgetDefaultsModal } from '../BudgetDefaultsModal';

export default function ViewBudgetsPage() {

  const budgetYearsLoadingState = useLoadingState();
  const budgetsLoadingState = useLoadingState();
  const auditLoadingState = useLoadingState();

  const customerData = useApplicationSelector((state: RootState): GetCustomerDataResponse => state.commodityManagement.customerData);
  const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);

  const [ years, setYears ] = useState<YearDataModel[]>([]);
  const [ budgets, setBudgets ] = useState<BudgetModel[]>([]);
  const [ selectedYears, setSelectedYears ] = useState<number[]>([]);
  const rows = useMemo(getReportRows, [budgets]);

  const [addBudgetsModalOpen, setAddBudgetsModalOpen] = useState(false);
  const [editBudgetsModalOpen, setEditBudgetsModalOpen] = useState(false);
  const [budgetDefaultsModalOpen, setBudgetDefaultsModalOpen] = useState(false);
  const [budgetToEdit, setBudgetToEdit] = useState<BudgetFilters>({});
  
  const [ auditTrailModalIsOpen, setAuditTrailModalIsOpen ] = useState(false);
  const [ AuditTrailModels, setAuditTrialChangeEvents ] = useState<AuditTrailModel[]>([]);

  function editBudgets(budgetFilter: BudgetFilters) {
    // This will work as add deal ticket if dealTicketId is undefined
    setBudgetToEdit(budgetFilter);
    setEditBudgetsModalOpen(true);
  }
  
  async function onBudgetsSave(budgetFilter: BudgetFilters) {
    
    var startDate = ContractDateHelper.getStartContractDate(budgetFilter.fiscalYear!, customerSettings!.fiscalMonthOffset);

    try {
      // Have to also refresh budget years 
      const yearsPromise = CommodityManagementApi.listBudgetYears(undefined).then((q) => setYears(q.data.rows ?? []));

      // load new budgets & update them
      const budgetsPromise = CommodityManagementApi.listBudgets(
        undefined,
        undefined,
        startDate.year,
        startDate.month,
        undefined,
        undefined,
        undefined,
        undefined,
        budgetFilter.fiscalYear ? [budgetFilter.fiscalYear] : undefined,
        budgetFilter.commodityId ? [budgetFilter.commodityId] : undefined,
        budgetFilter.locationId ? [budgetFilter.locationId] : undefined,
        budgetFilter.counterpartyId ? [budgetFilter.counterpartyId] : undefined,
        budgetFilter.budgetRoundId ? [budgetFilter.budgetRoundId] : undefined,
        ).then(res => {

        setBudgets(previous => {
          const newBudgets = [...previous];
          const responseRows = res.data.rows ?? [];
      
          // Replace existing budgets with new ones from response, or add if not found
          responseRows.forEach(newBudget => {
          const existingIndex = newBudgets.findIndex(b => b.budgetId === newBudget.budgetId);
          if (existingIndex >= 0) {
            newBudgets[existingIndex] = newBudget;
          } else {
            newBudgets.push(newBudget);
          }
          });
      
          return newBudgets;
        });
      });


      if (!selectedYears.includes(budgetFilter.fiscalYear!)) {
        setSelectedYears(prev => [...prev, budgetFilter.fiscalYear!]);
      }

      await yearsPromise;
      await budgetsPromise;
      
      setEditBudgetsModalOpen(false);
      setAddBudgetsModalOpen(false);
    }
    catch (err) {
    }
  }

  function onBudgetDefaultsSave() {
    setBudgetDefaultsModalOpen(false);
    
    // load new budgets & update them
    CommodityManagementApi.listBudgets(
        budgetsLoadingState.setLoadingState,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        selectedYears,
        undefined,
        undefined,
        undefined
        ).then(res => {
          // We refresh all the years
          setBudgets(res.data.rows ?? []);
      });
  }

  function getBudgetYears() {
    CommodityManagementApi.listBudgetYears(budgetYearsLoadingState.setLoadingState).then((q) => setYears(q.data.rows ?? []));
  }

  function getInitialBudgets() {
    const year = dayjs().year();
    var startDate = ContractDateHelper.getStartContractDate(year, customerSettings!.fiscalMonthOffset);

    CommodityManagementApi.listBudgets(
      budgetsLoadingState.setLoadingState,
      undefined,
      startDate.year,
      startDate.month,
    ).then((q) => {
      setBudgets(previous => uniqBy([...previous, ...q.data.rows ?? []], x => x.budgetId))
      const years = uniq(q.data.rows?.map(x => ContractDateHelper.getFiscalYear(x.budgetDate, customerSettings!.fiscalMonthOffset!)) ?? []);
      setSelectedYears(years);
    });
  }

  function addYearBudgets(year: number) {
    var startDate = ContractDateHelper.getStartContractDate(year, customerSettings!.fiscalMonthOffset);
    var endDate = ContractDateHelper.addMonths(startDate, 11);

    CommodityManagementApi.listBudgets(
      budgetsLoadingState.setLoadingState,
      undefined,
      startDate.year,
      startDate.month,
      undefined,
      endDate.year,
      endDate.month,
    ).then((q) => {
      setBudgets(previous => uniqBy([...previous, ...q.data.rows ?? []], x => x.budgetId))
      setSelectedYears(previous => [...previous, year]);
    });
  }

  function removeYearBudgets(year: number) {
    const startDate = ContractDateHelper.getStartContractDate(year, customerSettings?.fiscalMonthOffset);
    const endDate = ContractDateHelper.addMonths(startDate, 12);
    setSelectedYears(previous => previous.filter(x => x != year));
    setBudgets(previous => previous.filter(x => !(ContractDateHelper.isGreaterThanOrEqualTo(x.budgetDate, startDate) && ContractDateHelper.isLessThanOrEqualTo(x.budgetDate, endDate))));
  }

  function getReportRows(): BudgetRow[] {
    const rows: BudgetRow[] =  budgets.map(x => ({ path: [], budgetLabel: '' , ...x }) as unknown as BudgetRow);

    // Filter out undefined groupings
    // const selectedGroupings = [reportSettings?.behavior?.grouping?.[0], reportSettings?.behavior?.grouping?.[1]].filter(x => !!x).map(x => x!);
    const selectedGroupings: GroupingOption[] = ['BudgetYear'];

    // Update the path for each row based on groupings
    ModelToRowTransformer.updatePaths(rows, selectedGroupings);

    // Add budgetId to path to make each path unique
    rows.forEach(x => x.path.push(x.budgetId?.toString() ?? ''));

    return rows;
  }

  function closeAuditTrailModal() {
    setAuditTrailModalIsOpen(false);
    setAuditTrialChangeEvents([]);
  }

  function viewAuditTrail(budgetId: number) {
    setAuditTrailModalIsOpen(true);
    CommodityManagementApi.getBudgetAuditTrail(auditLoadingState.setLoadingState, budgetId.toString()).then(q => {
      setAuditTrialChangeEvents(q.data.rows ?? []);
    });
  }

  useEffect(() => {
    getBudgetYears();
    getInitialBudgets();
  }, []);
  
  return (
    <StoneXMainPage>
      <StoneXRow align="baseline">
        <h1>Budgets</h1>

        <span className="pull-right">
          <Button onClick={() => setAddBudgetsModalOpen(true)}>Add Budget</Button>
        </span>
      </StoneXRow>

      <StoneXRow>
        <StoneXSelectPills 
          label="Fiscal Year" 
          options={years.map(x => x.year)} 
          multiple={true}
          getOptionId={x => x} 
          getOptionLabel={x => `${x}`}
          getSortingValue={x => x} 
          value={selectedYears}
          onSelect={addYearBudgets}
          onDeselect={removeYearBudgets}
        />
        <StoneXLoading show={budgetsLoadingState.isLoading()} />
      </StoneXRow>

      {customerSettings?.usesBudgetRounds && budgetsLoadingState.isLoaded() && (
        <StoneXRow>
          <Button variant='text' style={{paddingLeft: 0}} onClick={() => setBudgetDefaultsModalOpen(true)}>Set Active Budget Rounds</Button>
        </StoneXRow>
      )}

      <StoneXDivider />

      <BudgetTable
        budgets={rows} 
        onEditBudget={editBudgets} 
        onViewAuditTrail={viewAuditTrail}
      />

      <AddBudgetsModal 
        open={addBudgetsModalOpen} 
        years={years} 
        onClose={() => setAddBudgetsModalOpen(false)} 
        onSave={onBudgetsSave} 
      />

      <UpdateBudgetsModal 
        open={editBudgetsModalOpen} 
        budgetFilters={budgetToEdit}
        onClose={() => setEditBudgetsModalOpen(false)} 
        onSave={onBudgetsSave} 
      />

      <BudgetDefaultsModal
        open={budgetDefaultsModalOpen}
        onClose={() => setBudgetDefaultsModalOpen(false)}
        onSave={onBudgetDefaultsSave}
      />

      {budgetsLoadingState.isLoaded() && rows.length == 0 && <p>No budgets found. Please revise the search criteria.</p>}

      <ViewAuditTrailModal
        AuditTrailModels={AuditTrailModels} 
        open={auditTrailModalIsOpen} 
        isLoading={auditLoadingState.isLoading()} 
        onClose={closeAuditTrailModal} 
      />
    </StoneXMainPage>
  );
}
